// @ts-nocheck

import {
  DateSpan,
  DeadlineFilter,
  EMPTY_WHEN_FILTER,
  MenuCategory,
  MenuItem,
  MenuItemAttribute,
  MenuItemOption,
  MenuPackage,
  MenuSection,
  AttrValues,
  DecodedItemSelection,
  PackagePart,
  PickedPackage,
  MenuStructure,
  WhenFilter
} from '../models/FSMenu';
import moment from 'moment';
import {Moment} from 'moment';
import {ConfigDeliverType, ConfigLocale, VenueConfig} from '../models/FSConfig';
import {Order, PrebookMenu} from '../models/Order';
import {Filter, FilterLogic, ICParentLookup} from "./filter-logic";
import {DeliveryType} from '../models/Identity';
import {Menu} from 'primeng/menu';
import {TranslateService} from '../services/translate.service';
import {FSVoucher} from '../models/FSVoucher';

export default class MoneyUtils {
  // Smakhäfte Online: package tickets cost 62.50 kr each, staff should see the single ticket price
  private static readonly VOUCHER_PRICE_OVERRIDES: Record<string, Record<string, number>> = {
    '5810512988209152': { 'idfd_matbiljett': 7000 },
    '5066549580791808': { 'idfd_matbiljett': 7000 },
  };

  static hasVoucherPriceOverrides(venueId: number | undefined): boolean {
    return String(venueId) in this.VOUCHER_PRICE_OVERRIDES;
  }

  static voucherPriceCent(venueId: number | undefined, voucher: FSVoucher): number {
    const overrides = this.VOUCHER_PRICE_OVERRIDES[String(venueId)];
    if (!overrides || !voucher.item_json) {
      return voucher.price_cent;
    }
    const itemId = JSON.parse(voucher.item_json)?.id?.split('§')[0];
    return overrides[itemId] ?? voucher.price_cent;
  }

  // Venue currency and locale, set from cfg.locale when the app state loads (AppStateService). Defaults keep the
  // Swedish behaviour for venues published before the server sent cfg.locale.
  private static currencyCode = 'SEK';
  private static numberLocale = 'sv-SE';
  private static countryCode = 'SE';
  // Written after the amount, as the server's money_tools formats these currencies ("5,00 kr")
  private static readonly SYMBOLS: Record<string, string> = { SEK: 'kr', NOK: 'kr', DKK: 'kr', EUR: '€', GBP: '£' };

  static configure(locale?: ConfigLocale) {
    if (!locale) return;
    this.currencyCode = locale.currency || this.currencyCode;
    this.numberLocale = locale.locale || this.numberLocale;
    this.countryCode = locale.country || this.countryCode;
  }
  static currency() { return this.currencyCode; }
  static locale() { return this.numberLocale; }
  static country() { return this.countryCode; }
  static symbol() { return this.SYMBOLS[this.currencyCode] ?? this.currencyCode; }

  static formatCent(cents: number, noDigits = false) {
    const v = cents / 100;
    if (noDigits) {
      return this.formatNoCents(v);
    } else {
      return this.format(v);
    }
  }
  static format(amount: number) {
    return `${amount.toLocaleString(this.numberLocale, { maximumFractionDigits: 2, minimumFractionDigits: 2 })} ${this.symbol()}`;
  }
  static formatNoCents(amount: number) {
    // if amount has no cents, don't show them
    if (Math.abs(Math.round(amount) - amount) < Number.EPSILON) {
      return `${amount.toLocaleString(this.numberLocale, { maximumFractionDigits: 0, minimumFractionDigits: 0 })} ${this.symbol()}`;
    } else {
      return this.format(amount);
    }
  }
}

export class LangUtils {
  // Our language codes are the primary subtag ("en", "no"); Norwegian browsers and the server say "nb"/"nn"
  static normalizeLang(code: string | null | undefined): string | null {
    if (!code) { return null; }
    const primary = code.split(/[-_]/)[0].toLowerCase();
    return primary === "nb" || primary === "nn" ? "no" : primary;
  }

  // The venue's own language (cfg.locale.language), "sv" for venues published before the server sent it
  static venueLanguage(cfg?: VenueConfig | null): string {
    return LangUtils.normalizeLang(cfg?.locale?.language) || "sv";
  }
}

export class MenuUtils {

  static readonly MENU_PAGE_FOOD = 'food';
  static readonly MENU_PAGE_DRINKS = 'drinks';

  static buildFilter(order: Order, cfg?: VenueConfig): Filter|null {
    const menuFilterStr = order.user_cfg?.menu_filter;
    if (menuFilterStr) {
      const filterExp = FilterLogic.parseFilterExpressions(menuFilterStr);
      return new Filter(filterExp, order.user_cfg, new ICParentLookup(cfg?.ic));
    }
    return null;
  }

  /** Whether a prebook guest menu's sections (compiled section or category ids) take this category: all do when
   *  there are none, as in beam's guest menu build. */
  static isPicked(menuSections: string[] | undefined, section: MenuSection, cat: MenuCategory): boolean {
    return !menuSections?.length || menuSections.includes(section.id) || menuSections.includes(cat.id);
  }

  static updatePageHiddenFlag(sections: MenuSection[], cfg: VenueConfig, order: Order, date: Moment, prop: string, hidePromo = false, matchOnlyWeekday = false,
                              whenTag?: WhenFilter[], itemFilter?: Filter) {
    const stags = order.user_cfg?.menu_filter_tag;
    const menuSections = order.user_cfg?.menu_sections;
    for (const section of sections) {
      let catVisible = false;
      for (const cat of section.categories) {
        let itemVisible = false;
        const picked = MenuUtils.isPicked(menuSections, section, cat);
        for (const item of cat.items) {

          if (!picked) {
            item[prop] = true;
          } else if (!item.hidden) {
            if (itemFilter) {
              item[prop] = !itemFilter.itemMatches(item);
            } else
              if (stags) {
              item[prop] = item.tags ? item.tags.indexOf(stags) === -1 : true;
            } else {
              item[prop] = false;
            }
            if (!item[prop]) {
              if (hidePromo && item.pic) {
                item[prop] = true;
              } else if (item.when || item.deadline) {
                const deadlineValid = item.deadline ? MenuUtils.deadlineValid(item.deadline, date) : true;
                if (!deadlineValid) {
                  item[prop] = true;
                } else if (item.when) {
                  if (whenTag) {
                    if (whenTag.length === 1 && whenTag[0] === EMPTY_WHEN_FILTER) {
                      item[prop] = false;
                    } else {
                      item[prop] = !MenuUtils.isWhenOverlapping(item.when, whenTag);
                    }
                  } else {
                    item[prop] = !MenuUtils.isWhenVisible(item, date, matchOnlyWeekday);
                  }
                }
              } else if (matchOnlyWeekday) {
                item[prop] = true;
              }
            }
          } else {
            item[prop] = item.hidden;
          }
          if (!item[prop]) {
            itemVisible = true;
          }
        }
        cat[prop] = !itemVisible;
        if (!cat[prop]) {
          catVisible = true;
        }
      }

      //count visible cats exclude promos
      let hasVisiblecCats = false;
      for (const cat of section.categories) {
        const vis = !cat[prop];
        const isPromo = cat.id === "PROMO";
        if (vis && !isPromo) {
          hasVisiblecCats = true;
          break;
        }
      }

      section[prop] = !hasVisiblecCats || !catVisible;
    }
  }


  static isWhenVisible(item: MenuItem, date: Moment, weekdayFilter = false): boolean {
    if (item.when !== undefined) {
      for (const w of item.when) {
        if (weekdayFilter) {
          const b = MenuUtils.isWhenAtWeekday(w, date);
          if (b) {
            return true;
          }
        } else {
          const b = MenuUtils.isWhenAtDate(w, date);
          if (b) {
            return true;
          }
        }
      }
      return false;
    }
    return !weekdayFilter;
  }

  static overlapWeekdays(wds1: number[], wds2: number[]): number[] {
    return wds1.filter(n => wds2.includes(n));
  }

  static overlapDateSpans(ds1: DateSpan, ds2: DateSpan): DateSpan | null {
    /*
        Check:
        ds1:    |------------------|
        ds2:  |---------------------|
        ds2:  |-----|
        ds2:      |-----|
        ds2:                     |-----|
        else no overlap
     */
    if (ds1.start.isBetween(ds2.start, ds2.end, undefined, "[]")) {
      if(ds1.end.isBetween(ds2.start, ds2.end, undefined, "[]")) {
        return new DateSpan(ds1.start, ds1.end);
      } else {
        return new DateSpan(ds1.start, ds2.end);
      }
    } else if (ds2.start.isBetween(ds1.start, ds1.end, undefined, "[]")) {
      if (ds2.end.isBetween(ds1.start, ds1.end, undefined, "[]")) {
        return new DateSpan(ds2.start, ds2.end);
      } else {
        return new DateSpan(ds2.start, ds1.end);
      }
    }
    return null;
  }

  static get30HMoment(h30: string): moment.Moment {
    const ps = h30.split(":");
    const h = parseInt(ps[0], 10);
    if( h >= 24 ) {
      return moment(`${h-24}:${ps[1]}`, "HH:mm").add(1, "day");
    } else {
      return moment(h30, "HH:mm");
    }
  }

  static formatH30FromMoment(m: moment.Moment): string {
    let h = m.hour();
    const min = m.minute();
    if (h<7) {
      h += 24;
    }
    return `${h.toFixed().padStart(2,'0')}:${min.toFixed().padStart(2, '0')}`;
  }

  static createWhenFilter(ds?: DateSpan, wds?: number[], bt30?: moment.Moment, et30?: moment.Moment) {
    const wf: WhenFilter = {};
    if (ds ) {
      wf.bd = ds.start.format("YYYYMMDD");
      wf.ed = ds.end.format("YYYYMMDD");
    }
    if (wds) {
      wf.wds = wds;
    }
    if (bt30) {
      wf.bt30 = this.formatH30FromMoment(bt30);
    }
    if (et30) {
      wf.et30 = this.formatH30FromMoment(et30);
    }
    return wf;
  }

  static overlapWhenFilter(wf1: WhenFilter, wf2: WhenFilter): WhenFilter | null {

    //Overlap weekdays
    let wds;
    if (wf1.wds && wf2.wds) {
      wds = this.overlapWeekdays(wf1.wds, wf2.wds);
      if (!wds.length) {
        return null;
      }
    } else if (wf1.wds) {
      wds = wf1.wds;
    } else if (wf2.wds) {
      wds = wf2.wds;
    }

    //Overlap dates
    let ds: DateSpan;
    if(wf1.bd && !wf2.bd) {
      ds = new DateSpan(moment(wf1.bd, "YYYYMMDD"),  moment(wf1.ed, "YYYYMMDD"));
    }
    if(!wf1.bd && wf2.bd) {
      ds = new DateSpan(moment(wf2.bd, "YYYYMMDD"),  moment(wf2.ed, "YYYYMMDD"));
    }
    if(wf1.bd && wf2.bd) {
      const ds1 = new DateSpan(moment(wf1.bd, "YYYYMMDD"),  moment(wf1.ed, "YYYYMMDD"));
      const ds2 = new DateSpan(moment(wf2.bd, "YYYYMMDD"),  moment(wf2.ed, "YYYYMMDD"));
      ds = this.overlapDateSpans(ds1, ds2);
      if (ds==null) {
        return null;
      }
    }

    //Check/filter weekdays in range
    if (ds && wds) {
      if (ds.end.diff(ds.start, "days") < 7) {
        const fwds = [];
        for (const mom = moment(ds.start); mom.diff(ds.end, 'days') <= 0; mom.add(1, 'days')) {
          const rwd = mom.isoWeekday() - 1;
          if (wds.includes(rwd)) {
            fwds.push(rwd);
          }
        }
        if (!fwds.length) {
          return null;
        }
        wds = fwds;
      }
    }

    //Overlap hours
    let bt30: moment.Moment;
    let et30: moment.Moment;
    // if (wf1.bt30 !== undefined && wf1.et30 !== undefined && wf2.bt30 !== undefined && wf2.et30 !== undefined) {
    //   const ds1 = new DateSpan(MenuUtils.get30HMoment(wf1.bt30), MenuUtils.get30HMoment(wf1.et30));
    //   const ds2 = new DateSpan(MenuUtils.get30HMoment(wf2.bt30), MenuUtils.get30HMoment(wf2.et30));
    //   const ho = this.overlapDateSpans(ds1, ds2);
    //   if (ho == null) {
    //     return null;
    //   }
    // }

    const bts = [];
    if (wf1.bt30 !== undefined) {
      bts.push(MenuUtils.get30HMoment(wf1.bt30));
    }
    if (wf2.bt30 !== undefined) {
      bts.push(MenuUtils.get30HMoment(wf2.bt30));
    }
    bt30 = bts.length ? moment.max(bts) : undefined;

    const ets = [];
    if (wf1.et30 !== undefined) {
      ets.push(MenuUtils.get30HMoment(wf1.et30));
    }
    if (wf2.et30 !== undefined) {
      ets.push(MenuUtils.get30HMoment(wf2.et30));
    }
    et30 = ets.length ? moment.min(ets) : undefined;

    if (bt30 && et30) {
      if (bt30.isSameOrAfter(et30)) {
        return null;
      }
    }

    return this.createWhenFilter(ds, wds, bt30, et30);
  }

  static overlapWhen(when1: WhenFilter[], when2: WhenFilter[]): WhenFilter[] {
    const overlaps = [];
    for (const wf1 of when1) {
      for (const wf2 of when2) {
        const r = this.overlapWhenFilter(wf1, wf2);
        if (r!=null) {
          overlaps.push(r);
        }
      }
    }
    return overlaps;
  }

  static isWhenOverlapping(when1: WhenFilter[], when2: WhenFilter[]): boolean {
    const whenr = this.overlapWhen(when1, when2);
    return whenr.length > 0;
  }

  static whenFilterHasPassed(when: WhenFilter, d: Moment): boolean {
    if (when.ed) {
      const ed = moment(when.ed, "YYYYMMDD");
      return ed.isBefore(d);
    }
    return false;
  }

  static whenHasPassed(when: WhenFilter[], d: Moment): boolean {
    for (const wf of when) {
      const r = this.whenFilterHasPassed(wf, d);
      if (!r) {
        return false;
      }
    }
    return true;
  }

  static deadlineValid(deadline: DeadlineFilter, d: Moment): boolean {
    let bd = deadline.bd ? moment(deadline.bd, "YYYYMMDD") : null;
    let ed = deadline.ed ? moment(deadline.ed, "YYYYMMDD") : null;
    if (!bd && !ed && deadline.wds) {
      const mind = deadline.wds[0];
      const maxd = deadline.wds[deadline.wds.length-1];
      bd = moment(d).isoWeekday(mind+1);
      ed = moment(d).isoWeekday(maxd+1);
      if (ed < bd) {
        const dwd = d.isoWeekday()-1;
        if (dwd > maxd) {
          ed.add(7, "days");
        } else {
          bd.add(-7, "days");
        }
      }
    }
    if (bd) {
      if (deadline.bt30) {
        const s = this.get30HMoment(deadline.bt30);
        bd.hour(s.hour()).minute(s.minute());
      } else {
        bd.hour(0).minute(0);
      }
    }
    if (ed) {
      if (deadline.et30) {
        const s = this.get30HMoment(deadline.et30);
        ed.hour(s.hour()).minute(s.minute());
      } else {
        ed.hour(23).minute(59);
      }
    }

    if (bd==null && ed==null) {
      if (deadline.rel_days != null) {
        return true;
      }
      console.error(`Deadline syntax error: ${JSON.stringify(deadline)}`);
      return false;
    }

    const bdok = bd==null || d.isSameOrAfter(bd);
    const edok = ed==null || d.isSameOrBefore(ed);
    return bdok && edok;
  }

  static isWhenAtDate(w: WhenFilter, d: Moment): boolean {
    let n = d.day() - 1;
    if (n < 0) {
      n = 6;
    }
    let ch = d.hour() + d.minute() / 60.0;
    if (ch < 6) {
      ch += 24;
    }

    //{wds: [1,2,3], bt30: "11:00", et30: "13:00", bd: "20180307", ed: "20180407"}
    let bwd = true;
    if (w.wds !== undefined) {
      bwd = w.wds.indexOf(n) > -1;
    }

    let btime = true;
    if (w.bt30 !== undefined) {
      const ps = w.bt30.split(":");
      const bth = parseInt(ps[0], 10) + parseInt(ps[1], 10) / 60.0;
      //console.log(bth);
      btime = ch > bth;
    }
    if (w.et30 !== undefined) {
      const ps = w.et30.split(":");
      const eth = parseInt(ps[0], 10) + parseInt(ps[1], 10) / 60.0;
      //console.log(eth);
      btime = btime && (ch <= eth);
    }

    let bdate = true;
    if (w.bd !== undefined && w.ed !== undefined) {
      const bd = moment(w.bd, "YYYYMMDD");
      const ed = moment(w.ed, "YYYYMMDD");
      bdate = d >= bd && d <= ed;
    } else if (w.bd !== undefined) {
      const bd = moment(w.bd, "YYYYMMDD");
      const ed = moment(bd).add(1, "days");
      bdate = d >= bd && d <= ed;
    }

    //console.log(`weekday-ok: ${bwd} time-ok: ${btime} date-ok: ${bdate}`);
    return bwd && btime && bdate;
  }

  static isWhenAtWeekday(w: WhenFilter, d: Moment): boolean {
    const n = d.isoWeekday() - 1;
    return w.wds?.includes(n) ?? false;
  }

  static getItemInMenu(itemId: string, menu: MenuStructure): MenuItem {
    let item: MenuItem = null;
    if (menu) {
      if (menu.food) {
        item = this.getItemPage(menu.food, itemId);
        if (item) { return item; }
      }
      if (menu.drinks) {
        item = this.getItemPage(menu.drinks, itemId);
        if (item) { return item; }
      }
    }
    return null;
  }

  static getItemPage(sections: MenuSection[], itemId: string): MenuItem | null {
    //console.log(`Searching for ${itemId}`);
    for (const section of sections) {
      const item = this.getItemSection(section, itemId);
      if (item) { return item; }
    }
    return null;
  }

  static getItemSection(section: MenuSection, itemId: string): MenuItem | null {
    for (const cat of section.categories) {
      const item = this.getItemCategory(cat, itemId);
      if (item) { return item; }
    }
    return null;
  }

  static getItemCategory(cat: MenuCategory, itemId: string): MenuItem | null {
    for (const it of cat.items) {
      if (it.id === itemId || it.id === "*") { return it; }
    }
    return null;
  }

  // The id-encoded value of one attribute selection, or null when it is left out of the id:
  // radio key, checkbox key when checked, multi as count++key++count2++key2
  static encodeAttributeValue(atr: MenuItemAttribute, sel: any): string | null {
    if (atr.multi) {
      // sel is {key: value, key2: value2}, pairs in the attribute's options order
      const value = atr.options
        .map(opt => [opt.key, sel?.[opt.key]])
        .filter(([_, count]) => count > 0)
        .map(([key, count]) => `${count}++${key}`)
        .join("++");
      return value.length > 0 ? value : null;
    }
    if (atr.options.length === 1) {
      // Checkbox: the key when checked, else left out
      const opt = atr.options.find(o => o.default) ?? atr.options[0];
      return sel ? opt.key : null;
    }
    if (sel == null) {
      return null;
    }
    // Hidden attributes left at their default stay out of the id
    const defaultKey = (atr.options.find(o => o.default) ?? atr.options[0])?.key;
    if (atr.hidden && sel === defaultKey) {
      return null;
    }
    return sel;
  }

  // The names of what was chosen on visible attributes: a radio's option, a ticked checkbox, a multi's counts
  static attributeValueNames(attributes: MenuItemAttribute[] | undefined, values: AttrValues | undefined): string[] {
    const names: string[] = [];
    for (const atr of attributes ?? []) {
      if (atr.hidden === true) continue;
      const sel = values?.[atr.id];
      if (atr.multi) {
        for (const opt of atr.options) {
          const n = (sel as Record<string, number> | undefined)?.[opt.key] ?? 0;
          if (n > 0) names.push(n > 1 ? `${n} × ${opt.value}` : opt.value);
        }
      } else if (atr.options.length === 1) {
        if (sel) names.push(atr.options[0].value);
      } else {
        const opt = atr.options.find(o => o.key === sel);
        if (opt) names.push(opt.value);
      }
    }
    return names;
  }

  static buildFullItemId(item: MenuItem, attrValues: {}, parts?: PackagePart[]): string {
    const ids = [];
    for (const atr of item?.attributes ?? []) {
      const value = this.encodeAttributeValue(atr, attrValues?.[atr.id]);
      if (value != null) {
        ids.push(atr.id + "|" + value);
      }
    }
    // A package's picks in choice order, then each part's own attributes by part index
    for (const part of parts ?? []) {
      if (part.choiceIndex != null) {
        ids.push("_pkg_|" + part.id);
      }
    }
    (parts ?? []).forEach((part, index) => {
      for (const atr of part.menuItem?.attributes ?? []) {
        const value = this.encodeAttributeValue(atr, part.attrValues?.[atr.id]);
        if (value != null) {
          ids.push(`_p${index}_${atr.id}|${value}`);
        }
      }
    });
    return ids.length > 0 ? item.id + "%%%%" + ids.join("%%") : item.id;
  }

  // The inverse of encodeAttributeValue: what the guest selected, from the value as it is in the id.
  // A checkbox is checked by being there, a multi is count++key pairs.
  static decodeAttributeValue(atr: MenuItemAttribute, encoded: string): string | boolean | Record<string, number> {
    if (atr.multi) {
      const counts: Record<string, number> = {};
      for (const opt of atr.options) {
        counts[opt.key] = 0;
      }
      const tokens = encoded.split("++");
      for (let i = 0; i + 1 < tokens.length; i += 2) {
        const n = parseInt(tokens[i], 10);
        if (n > 0 && tokens[i + 1] in counts) {
          counts[tokens[i + 1]] = n;
        }
      }
      return counts;
    }
    return atr.options.length === 1 ? true : encoded;
  }

  // The key|value pairs of an encoded item id, in order. The comment hash (after %%||) is not a pair.
  // An item id without the server's markers, as the server reads it (pay_tools.clean_item_id): a takeaway row's
  // id ends in §FF=TAKEAWAY§ and a discounted one has DISCOUNT<n>CENT
  static cleanItemId(id: string): string {
    return id.replace(/§FF=TAKEAWAY§/g, "").replace(/DISCOUNT\d+CENT/, "");
  }

  static itemIdAttributePairs(fullId: string): [string, string][] {
    const pairs: [string, string][] = [];
    for (const section of this.cleanItemId(fullId).split("%%||")[0].split("%%%%").slice(1)) {
      for (const attrib of section.split("%%")) {
        const sep = attrib.indexOf("|");
        if (sep > 0) {
          pairs.push([attrib.slice(0, sep), attrib.slice(sep + 1)]);
        }
      }
    }
    return pairs;
  }

  // Attribute values from an id's pairs, over `base` (the defaults): what's left out of the id is an unchecked
  // checkbox, a multi at 0 or a hidden attribute at its default. A radio key the menu no longer has is ignored.
  private static applyAttributePairs(attributes: MenuItemAttribute[] | undefined, pairs: [string, string][], base: AttrValues): AttrValues {
    const values = {...base};
    for (const atr of attributes ?? []) {
      const encoded = pairs.find(([key]) => key === atr.id)?.[1];
      if (encoded == null) {
        if (!atr.multi && atr.options.length === 1) {
          values[atr.id] = false;
        }
        continue;
      }
      if (!atr.multi && atr.options.length > 1 && !atr.options.some(o => o.key === encoded)) {
        continue;
      }
      values[atr.id] = this.decodeAttributeValue(atr, encoded);
    }
    return values;
  }

  // The inverse of buildFullItemId: what the guest selected on an item already ordered, so it can be changed.
  // The item's own attribute values, the picks per choice (as for packageParts) and each part's attribute values by
  // part key. A pick the package no longer offers is dropped; the parts after it would shift, so only the fixed
  // parts then keep their attributes.
  static decodeItemSelection(item: MenuItem, fullId: string, lookup: (id: string) => MenuItem | null): DecodedItemSelection {
    const own: [string, string][] = [];
    const picks: string[] = [];
    const partPairs: Record<number, [string, string][]> = {};
    for (const [key, value] of this.itemIdAttributePairs(fullId)) {
      const part = /^_p(\d+)_(.+)$/.exec(key);
      if (key === "_pkg_") {
        picks.push(value);
      } else if (part) {
        (partPairs[Number(part[1])] ??= []).push([part[2], value]);
      } else {
        own.push([key, value]);
      }
    }
    const attrValues = this.applyAttributePairs(item.attributes, own, this.defaultAttributeValues(item.attributes));

    const choices = item.package?.choices ?? [];
    const pkgCounts: Record<string, number>[] = choices.map(() => ({}));
    const picked = (ci: number) => Object.values(pkgCounts[ci]).reduce((acc, c) => acc + c, 0);
    let droppedPick = false;
    for (const pick of picks) {
      // Like the server: the first choice with room left that has the option
      const ci = choices.findIndex((choice, i) => picked(i) < choice.count && choice.options.some(o => o.id === pick));
      if (ci < 0) {
        droppedPick = true;
        continue;
      }
      pkgCounts[ci][pick] = (pkgCounts[ci][pick] ?? 0) + 1;
    }

    const partValues: Record<string, AttrValues> = {};
    if (item.package) {
      this.packageParts(item.package, pkgCounts, lookup).forEach((part, i) => {
        const attributes = part.menuItem?.attributes;
        const pairs = droppedPick && part.choiceIndex != null ? [] : (partPairs[i] ?? []);
        partValues[part.key] = this.applyAttributePairs(attributes, pairs, this.defaultAttributeValues(attributes, true));
      });
    }
    return {attrValues, pkgCounts, partValues};
  }

  // A package the guest can change in the cart: one with choices, or whose fixed parts have attributes to pick
  static isPackageChangeable(item: MenuItem | null | undefined, lookup: (id: string) => MenuItem | null): boolean {
    const pkg = item?.package;
    if (pkg == null) {
      return false;
    }
    return (pkg.choices?.length ?? 0) > 0 ||
      (pkg.included ?? []).some(inc => lookup(inc.id)?.attributes?.some(atr => atr.hidden !== true) ?? false);
  }

  // Attribute values before the guest picks: multi counts at 0, a radio at its default (or the first option).
  // With leaveForcedEmpty a forced radio starts unanswered, so the guest must pick.
  static defaultAttributeValues(attributes: MenuItemAttribute[] | undefined, leaveForcedEmpty = false): AttrValues {
    const values: AttrValues = {};
    for (const atr of attributes ?? []) {
      if (atr.multi) {
        const m: Record<string, number> = {};
        for (const opt of atr.options) {
          m[opt.key] = 0;
        }
        values[atr.id] = m;
      } else if (!(leaveForcedEmpty && atr.forced && atr.options.length > 1)) {
        const opt = atr.options.find(o => o.default) ?? (atr.options.length > 1 ? atr.options[0] : undefined);
        if (opt) {
          values[atr.id] = opt.key;
        }
      }
    }
    return values;
  }

  // Whether the guest has answered an attribute well enough to order: a forced one must have a pick,
  // a multi one at least its minimum
  static isAttributeAnswered(atr: MenuItemAttribute, sel: any): boolean {
    if (atr.multi) {
      const total = Object.values(sel ?? {}).reduce((acc: number, c: any) => acc + (Number(c) || 0), 0);
      return total >= (atr.multi.min ?? 0) && (!atr.forced || total > 0);
    }
    if (!atr.forced) {
      return true;
    }
    if (atr.options.length === 1) {
      return !!sel;
    }
    return atr.options.some(o => o.key === sel);
  }

  // The relative surcharges (price_diff, e.g. "+25") of an attribute selection. Absolute option prices are not counted.
  static attributePriceDiff(atr: MenuItemAttribute, sel: any): number {
    const diff = (opt?: MenuItemOption) => opt?.price_diff ? (parseFloat(opt.price_diff) || 0) : 0;
    if (atr.multi) {
      return atr.options.reduce((acc, opt) => acc + (Number(sel?.[opt.key]) || 0) * diff(opt), 0);
    }
    if (atr.options.length === 1) {
      return sel ? diff(atr.options.find(o => o.default) ?? atr.options[0]) : 0;
    }
    return diff(atr.options.find(o => o.key === sel));
  }

  // The menu price of a package: its fixed parts plus each choice's base for every pick
  static packageMenuPrice(pkg: MenuPackage | undefined): number {
    const fixed = pkg?.included?.reduce((acc, inc) => acc + (inc.price ?? 0), 0) ?? 0;
    const choices = pkg?.choices?.reduce((acc, ch) => acc + ch.count * (ch.base ?? 0), 0) ?? 0;
    return fixed + choices;
  }

  // The parts of a package: the fixed ones in order, then one per pick (choice order, option order, repeats).
  // `counts[choiceIndex][optionId]` is how many times the guest picked that option.
  static packageParts(pkg: MenuPackage | undefined, counts: Record<string, number>[],
                      lookup: (id: string) => MenuItem | null): PackagePart[] {
    const parts: PackagePart[] = [];
    (pkg?.included ?? []).forEach((inc, i) => {
      parts.push({key: "f" + i, id: inc.id, name: inc.name, menuItem: lookup(inc.id)});
    });
    (pkg?.choices ?? []).forEach((choice, ci) => {
      for (const opt of choice.options) {
        const n = counts[ci]?.[opt.id] ?? 0;
        for (let copy = 0; copy < n; copy++) {
          parts.push({key: `c${ci}_${opt.id}_${copy}`, id: opt.id, name: opt.name, menuItem: lookup(opt.id),
            choiceIndex: ci, surcharge: (opt.price ?? 0) - (choice.base ?? 0)});
        }
      }
    });
    return parts;
  }

  // Every choice has exactly its count of picks, and every visible part attribute is answered
  static isPackageSelectionComplete(pkg: MenuPackage | undefined, counts: Record<string, number>[], parts: PackagePart[]): boolean {
    const choicesDone = (pkg?.choices ?? []).every((choice, ci) =>
      Object.values(counts[ci] ?? {}).reduce((acc, c) => acc + c, 0) === choice.count);
    return choicesDone && parts.every(part => (part.menuItem?.attributes ?? [])
      .filter(atr => atr.hidden !== true)
      .every(atr => this.isAttributeAnswered(atr, part.attrValues?.[atr.id])));
  }

  // What the guest pays for a package as selected: menu price + pick surcharges + part attribute surcharges
  static packageSelectionPrice(pkg: MenuPackage | undefined, parts: PackagePart[]): number {
    let total = this.packageMenuPrice(pkg);
    for (const part of parts) {
      total += part.surcharge ?? 0;
      for (const atr of part.menuItem?.attributes ?? []) {
        total += this.attributePriceDiff(atr, part.attrValues?.[atr.id]);
      }
    }
    return total;
  }

  // Under a package placeholder row: what its component rows (same pid) are, when the guest chose or
  // customized something. Null for a plain set package, which shows as before.
  static packageDetailLines(placeholder: any, rows: any[], menu: MenuStructure | null): string[] | null {
    const pid = placeholder?.package?.pid;
    if (pid == null) {
      return null;
    }
    const components = rows.filter(r => r !== placeholder && r.package?.pid === pid && r.package?.aggregated_cent == null);
    const customized = components.some(r => (r.id ?? "").includes("%%%%"));
    const menuItem = menu && placeholder.package.id ? this.getItemInMenu(placeholder.package.id, menu) : null;
    if (components.length === 0 || (!customized && !(menuItem?.package?.choices?.length > 0))) {
      return null;
    }
    // Two of the same pick are one row with a count (2 × Patatas bravas)
    return components.map(r => {
      const name = (r.count ?? 1) > 1 ? `${r.count} × ${r.name}` : r.name;
      return r.customized ? `${name}: ${r.customized}` : name;
    });
  }

  // A ticked package that needed picking goes with its picks (`picked`), anything else with its attributes
  static collectSelectedItemIds(selectedItems, attrValues, menuStruct, picked: Record<string, PickedPackage> = {}) {
    const itemIds = [];
    for (const key of Object.keys(selectedItems)) {
      const isSelected = selectedItems[key];
      if (isSelected) {
        console.log(key);
        const selectedItem = MenuUtils.getItemInMenu(key, menuStruct);
        const fullItemId = picked[key]?.id ?? MenuUtils.buildFullItemId(selectedItem, attrValues[key]);
        itemIds.push(fullItemId);
      }
    }
    console.log(itemIds);
    return itemIds;
  }

  // A package as picked in the item view: its id, what to show under it and its price. A line per pick and per part
  // with attributes answered (Entrecôte: Pepparsås); identical lines count up (2 × Patatas bravas).
  static pickedPackage(item: MenuItem, attrValues: AttrValues, parts: PackagePart[]): PickedPackage {
    const counts = new Map<string, number>();
    for (const part of parts) {
      const chosen = this.attributeValueNames(part.menuItem?.attributes, part.attrValues);
      if (part.choiceIndex == null && chosen.length === 0) continue;
      const line = chosen.length > 0 ? `${part.name}: ${chosen.join(", ")}` : part.name;
      counts.set(line, (counts.get(line) ?? 0) + 1);
    }
    const lines = [...counts].map(([line, n]) => n > 1 ? `${n} × ${line}` : line);
    return {id: this.buildFullItemId(item, attrValues, parts), lines, price: this.packageSelectionPrice(item.package, parts)};
  }

  static compareTableRegex(regex: string, table: string): boolean {
    if (table && regex) {
      const re = new RegExp(regex, "i");
      const arr = table.match(re);
      return arr !== null;
    }
    return false;
  }

  static deriveDeliverType(table: string, config: VenueConfig): string {
    if (MenuUtils.compareTableRegex(config.dt?.takeaway?.table_regex, table)) {
      return DeliveryType.Takeaway;
    }
    if (MenuUtils.compareTableRegex(config.dt?.roomservice?.table_regex, table)) {
      return DeliveryType.Roomservice;
    }
    if (MenuUtils.compareTableRegex(config.dt?.pickup?.table_regex, table)) {
      return DeliveryType.Pickup;
    }
    if (MenuUtils.compareTableRegex(config.dt?.foodcourt?.table_regex, table)) {
      return DeliveryType.Foodcourt;
    }
    return DeliveryType.Table;
  }

  static getDeliverTypeConfig(dt: string|undefined, config: VenueConfig): ConfigDeliverType {
    if (dt === DeliveryType.Takeaway) {
      return config.dt.takeaway;
    }
    if (dt === DeliveryType.Roomservice) {
      return config.dt.roomservice;
    }
    if (dt === DeliveryType.Pickup) {
      return config.dt.pickup;
    }
    if (dt === DeliveryType.Foodcourt) {
      return config.dt.foodcourt;
    }
    return config.dt.inhouse;
  }

  static isDeliverTypeOpen(dt: string|undefined, config: VenueConfig): boolean {
    const dtc = this.getDeliverTypeConfig(dt, config);
    return dtc?.open ?? false;
  }

  static getRootPromoImgUrl(food: MenuSection[]): string {
    if (!food) {
      return null;
    }
    const root = food.find( s => s.name === "root");
    if (root && root.categories) {
      if (root.categories[0].items && root.categories[0].items.length > 0) {
        const i1 = root.categories[0].items[0];
        return i1.pic;
      }
    }
    return null;
  }

  static getSectionPromoImgUrl(section: MenuSection): string {
    if (section && section.categories) {
      if (section.categories[0].items && section.categories[0].items.length > 0) {
        const i1 = section.categories[0].items[0];
        return i1.pic;
      }
    }
    return null;
  }


  // 15/4 18-19
  // mån-fre 7-12
  // mån-ons 15/4-17/8 18-19
  static readableWhenFilter(wf: WhenFilter): string {
    if (wf == null) { return null; }
    const parts = [];
    if (wf.wds?.length) {
      if (wf.wds.length>1) {
        const mind = wf.wds[0];
        const maxd = wf.wds[wf.wds.length-1];
        const s = `${TimeUtils.getWDShortName(mind+1)}-${TimeUtils.getWDShortName(maxd+1)}`;
        parts.push(s);
      } else {
        const s = `${TimeUtils.getWDShortName(wf.wds[0]+1)}`;
        parts.push(s);
      }
    }

    if (wf.bd || wf.ed) {
      const dParts = [];
      let sameDate = false;
      if (wf.bd) {
        const bd = moment(wf.bd, "YYYYMMDD");
        dParts.push(bd.format("D/M"));
        if (wf.ed === wf.bd) {
          sameDate = true;
        }
      }
      if (wf.ed && !sameDate) {
        const ed = moment(wf.ed, "YYYYMMDD");
        dParts.push(ed.format("D/M"));
      }
      if (dParts.length) {
        if (parts.length) {
          parts.push(`(${dParts.join(" - ")})`);
        } else {
          parts.push(dParts.join(" - "));
        }
      }
    }

    const hmParts = [];
    if (wf.bt30) {
      hmParts.push(MenuUtils.get30HMoment(wf.bt30).format("HH:mm"));
    }
    if (wf.et30) {
      hmParts.push(MenuUtils.get30HMoment(wf.et30).format("HH:mm"));
    }
    if (hmParts.length) {
      parts.push(hmParts.join(" - "));
    }

    return parts.join(" ");
  }

  static readableWhenDeadline(item: MenuItem): string {
    const rw = MenuUtils.readableWhen(item.when);
    const dl = item.deadline;
    const isRelWindow = dl != null && dl.rel_days != null && !!dl.bt30 && !!dl.et30;
    const rd = MenuUtils.readableDeadline(dl);
    if (rw || rd) {
      const rwd = [];
      if (rw) { rwd.push(`Serveras ${rw}`); }
      if (rd) { rwd.push(isRelWindow ? rd : `Beställ senast ${rd}`); }
      return rwd.join(", ");
    }
    return null;
  }

  static readableWhen(when: WhenFilter[]): string {
    if (when == null) { return null; }
    const parts = [];
    for (const wf of when) {
      parts.push(this.readableWhenFilter(wf));
    }
    return parts.join(", ");
  }

  static readableDeadline(deadline: DeadlineFilter): string {
    if (deadline == null) { return null; }
    // Relative order window: rel_days with both bt30 and et30 → full sentence incl. day reference.
    if (deadline.rel_days != null && deadline.bt30 && deadline.et30) {
      const bt = MenuUtils.get30HMoment(deadline.bt30).format("HH:mm");
      const et = MenuUtils.get30HMoment(deadline.et30).format("HH:mm");
      if (deadline.rel_days === 0) {
        return `Går att beställa samma dag mellan ${bt}-${et}`;
      }
      const days = ["dagen", "två dagar", "tre dagar", "fyra dagar", "fem dagar"];
      const dayLabel = deadline.rel_days <= days.length ? days[deadline.rel_days-1] : `${deadline.rel_days} dagar`;
      return `Går att beställa mellan ${bt}-${et} ${dayLabel} innan`;
    }
    const parts = [];
    if (deadline.wds?.length) {
      const maxd = deadline.wds[deadline.wds.length-1];
      parts.push(TimeUtils.getWDShortName(maxd+1));
    } else if (deadline.ed) {
      const ed = moment(deadline.ed, "YYYYMMDD");
      parts.push(ed.format("D/M"));
    }
    if (deadline.et30) {
      parts.push(MenuUtils.get30HMoment(deadline.et30).format("HH:mm"));
    }
    if (deadline.rel_days) {
      //dagen innan, två dagar innan etc.
      const days = ["dagen", "två dagar", "tre dagar", "fyra dagar", "fem dagar"];
      if (deadline.rel_days < days.length) {
        parts.push(days[deadline.rel_days-1]);
      } else {
        parts.push(`${deadline.rel_days} dagar`);
      }
      parts.push('innan');
    }
    return parts.join(" ");
  }

  /** The guest menus a prebooking orders from, a page each in place of food and drinks; none outside prebook. */
  static prebookMenus(appState: AppState): PrebookMenu[] {
    return appState.order?.prebook?.template?.menus ?? [];
  }

  /** A prebook guest menu's page: the categories it picks out of the food and drinks pages, under its own filter,
   *  laid out straight on the page as the booking page shows guest menus (a section's own dishes under its name).
   *  Copies, so a dish can show in two guest menus with different filters. */
  static guestMenuSections(appState: AppState, menu: PrebookMenu): MenuSection[] {
    const order = {...appState.order, user_cfg: {...appState.order.user_cfg, menu_sections: menu.sections, menu_filter: menu.filter}};
    const sections = [...(appState.menu.food ?? []), ...(appState.menu.drinks ?? [])].map(section =>
      ({...section, categories: section.categories.map(cat => ({...cat, items: cat.items.map(item => ({...item}))}))}));
    this.updatePageHiddenFlag(sections, appState.cfg, order, moment(), "mhide", false, false, order.user_cfg?.when_tag,
      this.buildFilter(order, appState.cfg));
    const categories = sections.flatMap(section => section.categories.map(cat =>
      section.id !== '__root' && (!cat.name || cat.name === '__root') ? {...cat, name: section.name} : cat));
    return [{id: '__root', name: 'root', categories}];
  }

  static getPageSections(appState: AppState, pageId: string): MenuSection[] {
    const prebookMenu = this.prebookMenus(appState).find(menu => menu.id === pageId);
    if (prebookMenu) {
      return this.guestMenuSections(appState, prebookMenu);
    }
    if (pageId === this.MENU_PAGE_FOOD) {
      return appState.menu.food;
    }
    if (pageId === this.MENU_PAGE_DRINKS) {
      return appState.menu.drinks;
    }
    return null;
  }

  static getSection(appState: AppState, pageId: string, sectionId: string): MenuSection {
    console.log(pageId, sectionId);
    const sections = this.getPageSections(appState, pageId);
    console.log(sections);
    if (sections) {
      return sections.find(s => s.id === sectionId);
    }
  }

  static getPageTopImage(appState: AppState, pageId: string): string {
    const sections = this.getPageSections(appState, pageId);
    return this.getRootPromoImgUrl(sections);
  }

  static filterRootCategories(rootCategories = this.root): MenuCategory[] {
    return rootCategories?.categories.filter( s => !s.mhide && s.id != "PROMO" );
  }

  static filterSections(sections: MenuSection[]): MenuSection[] {
    sections = sections?.filter( s => !s.mhide );
    return sections;
  }

  static getPagesWithVisibleItems(appState: AppState): string[] {
    const prebookMenus = this.prebookMenus(appState);
    if (prebookMenus.length) {
      return prebookMenus.filter(menu => this.hasVisibleItems(this.guestMenuSections(appState, menu))).map(menu => menu.id);
    }
    const pages = [];
    if (this.hasVisibleItems(appState.menu.food)) {
      pages.push(this.MENU_PAGE_FOOD);
    }
    if (this.hasVisibleItems(appState.menu.drinks)) {
      pages.push(this.MENU_PAGE_DRINKS);
    }
    return pages;
  }

  private static hasVisibleItems(sections: MenuSection[]): boolean {
    const hasVisibleSections = MenuUtils.hasVisibleSections(this.filterSections(sections));
    const rootSection = sections?.find( s => s.id === '__root');
    return hasVisibleSections || MenuUtils.filterRootCategories(rootSection)?.length > 0;
  }

  static hasVisibleSections(visibleSections: MenuSection[]) {
    if ( visibleSections == null ) {
      //console.log("hasVisibleSections (false) visibleSections == null");
      return false;
    }
    for (const section of visibleSections) {
      if (section.id !== "__root") {
        //console.log("hasVisibleSections (true) section.id " + section.id);
        return true;
      }
    }
    //console.log("hasVisibleSections (false)");
    return false;
  }

  static hasVisibleRootCategories(visibleRootCategories: MenuCategory[] = this.visibleRootCategories) {
    if ( visibleRootCategories == null ) {
      console.log("visibleRootCategories (false) visibleRootCategories == null");
      return false;
    }
    for (const category of visibleRootCategories) {
      if (category.id !== "PROMO") {
        console.log("visibleRootCategories (true) category.id " + category.id);
        return true;
      }
    }
    console.log("visibleRootCategories (false)");
    return false;
  }

  static isDrink(item: MenuItem) {
    //TODO make this better
    return item.id.startsWith("iddrk");
  }

  static getItemCount(items: OrderItem[] | undefined) {
    if (items == null) {
      return 0;
    }
    // One package = its placeholder and component rows, which share pid (two of the same package are two)
    const countedPackages = new Set<string>();
    return items.reduce((acc, item) => {
      if (item.package && countedPackages.has(item.package.pid)) {
        return acc;
      }
      if (item.package) {
        countedPackages.add(item.package.pid);
      }
      return acc + item.count;
    }, 0);
  }

  // dtcfg is undefined for a guest without a deliver type at a venue without an inhouse block (inhouse.active off):
  // a payment-link guest at a pay-only venue. Callers fall back to the venue name / "Välj Bord".
  static getDTSubTitle(appState: AppState, dtcfg: ConfigDeliverType | undefined, translate: TranslateService): string | undefined {
    if (appState.order?.user_cfg?.dt === "table") {
      return appState.order.user_cfg.table;
    }
    if (appState.order?.user_cfg?.dt === "roomservice") {
      const roomNumber = appState.order.user_cfg.roomnumber;
      if (roomNumber != null) {
        // One room per table: the room is the table name, e.g. "Svit 1"
        return dtcfg?.room_per_table ? roomNumber : `Rum ${roomNumber}`;
      } else {
        return "Välj rum";
      }
    } else {
      return dtcfg?.title ? translate.getString(dtcfg.title) : undefined;
    }
  }
}

export class TimeUtils {
  static getCurrentH30(): number {
    // Todo not timzone adjusted
    let h = moment().hour();
    if (h < 6) {
      h += 24;
    }
    return h + (moment().minute() / 60.0);
  }

  static getWeekDayName(wd) {
    const names = ["måndag", "tisdag", "onsdag", "torsdag", "fredag", "lördag", "söndag"];
    return names[wd - 1];
  }

  static getWDShortName(wd) {
    const names = ["mån", "tis", "ons", "tors", "fre", "lör", "sön"];
    return names[wd - 1];
  }

  // Prebook edit lock: naive UTC ISO from the server -> local "kl. 16:00", prefixed
  // with the date when the deadline is not today ("22 aug. kl. 16:00")
  static formatDeadlineClock(naiveUtcIso: string): string {
    const at = new Date(naiveUtcIso + "Z");
    const hh = String(at.getHours()).padStart(2, "0");
    const mm = String(at.getMinutes()).padStart(2, "0");
    const sameDay = at.toDateString() === new Date().toDateString();
    const day = sameDay ? "" : at.toLocaleDateString("sv-SE", { day: "numeric", month: "short" }) + " ";
    return day + "kl. " + hh + ":" + mm;
  }

  static getRelativeDayStringTimeDate(d: moment.Moment) {
    const now = moment();
    const dayDiff = now.dayOfYear() - d.dayOfYear();
    if (dayDiff === 0) {
      return "Idag kl " + d.format("HH:mm");
    } else if (dayDiff === -1) {
      return "Imorgon kl " + d.format("HH:mm");
    } else {
      const wdn = TimeUtils.getWeekDayName(d.isoWeekday());
      return wdn + " " + d.format("D/M HH:mm");
    }
  }
}

export class HackUtils {
  static shouldScroll(scd: {count: number, start?: moment.Moment}): boolean {
    if (scd.start) {
      // Scroll if not older than 500ms
      return moment().diff(scd.start) < 500;
    } else {
      // Scroll if not more than
      scd.start = moment();
      scd.count++;
      return scd.count < 5;
    }
  }

  static isMobile(): boolean {
    let check = false;
    // @ts-ignore
    // tslint:disable-next-line:only-arrow-functions
    (function(a) {if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) { check = true; }})(navigator.userAgent || navigator.vendor || window.opera);
    return check;
  }

  static isMobileiOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  }

  static DLG(d: {}): any {
    const w = window.innerWidth;
    const mobile = this.isMobile();
    let e: {};
    if (w < 600) {
      e = {
        minWidth: "95vw",
        position: {top: "8px"},
      };
    } else {
      e = {
        minWidth: "460px",
        maxWidth: "460px",
        position: {top: "16px"},
      };
    }
    return {...d, ...e};
  }

  static getBrowserInfo() {
    return {width: window.innerWidth, height: window.innerHeight};
  }

  /* tslint:disable:no-bitwise */
  static simpleBitHashHex(str: string): string {
    let hash = 0;
    if (str.length === 0) { return hash.toString(16); }
    let i;
    let chr;
    for (i = 0; i < str.length; i++) {
      chr = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + chr;
      hash |= 0; // Convert to 32bit integer
    }
    if (hash < 0) {
      //Convert to positive
      hash = hash + 8589934592;
    }
    return hash.toString(16);
  }

  static isWhitespace(str) {
    return /^\s*$/.test(str);
  }

}

export class PhoneUtils {
  static convertNumberIntoE164Format(phoneNumber: string, countryCode: string = "46"): string {
    phoneNumber = phoneNumber.trim();

    if (phoneNumber.startsWith("+")) {
      // Remove all non-digit characters except '+'
      phoneNumber = "+" + phoneNumber.substring(1).replace(/[^0-9]/g, "");
    } else if (phoneNumber.startsWith("0")) {
      // Remove all non-digit characters first
      phoneNumber = phoneNumber.replace(/[^0-9]/g, "");
      // Remove leading zeros
      phoneNumber = phoneNumber.replace(/^0+/, "");
      // Prepend the country code and '+'
      phoneNumber = "+" + countryCode + phoneNumber;
    } else {
      // For all other cases, first remove all non-digit characters
      const cleanNumber = phoneNumber.replace(/[^0-9]/g, "");

      // Check if we have any digits left
      if (cleanNumber.length < 7) {
        throw new Error("Invalid phone number format");
      }

      // Prepend the country code and '+'
      phoneNumber = "+" + countryCode + cleanNumber;
    }

    // Check case +460xxx => +46xxx
    if (phoneNumber.startsWith("+460")) {
      phoneNumber = phoneNumber.replace("+460", "+46");
    }

    return phoneNumber;
  }

}

export class MiscUtils {

  // Will find parameter in the snapshot hierarchy
  static getRouteParam(snapshot: ActivatedRouteSnapshot, key: string) {
    let v = snapshot.paramMap.get(key);
    if (v == null && snapshot.parent != null) {
      v = snapshot.parent.paramMap.get(key);
    }
    return v;
  }
}

export class ErrorUtils {
  // True when a wallet payment was dismissed by the user. Google Pay rejects with
  // { statusCode: 'CANCELED' }, Apple Pay with a 'cancel' event — neither is a real failure.
  static isUserCancel(error: any): boolean {
    if (!error) return false;
    if (error.statusCode === 'CANCELED' || error.type === 'cancel') return true;
    const msg = (error.message ?? error.statusMessage ?? '').toString().toLowerCase();
    return msg.includes('user closed') || msg.includes('canceled') || msg.includes('cancelled');
  }

  static getErrorMessage(error: any): string {
    if (!error) {
      return "Ett fel uppstod";
    }

    // HTTP Error response
    if (error.error?.message) {
      return error.error.message;
    }

    // Regular Error
    if (error.message) {
      return error.message;
    }

    return "Ett fel uppstod";
  }
}
